import {
    Card,
    Colors,
    Container,
    Heading,
    Icon,
    MultiSelectOutlined,
    Pagination,
    SearchBar,
    Text,
} from '@amelio/ui-library';
import { useMemo } from 'react';

import { SelectRevieweeResults } from './SelectRevieweeResults';

import type { SelectRevieweeContentProps } from '../../types';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { getAttributeEntriesWithSelectedValues } from '@/utils/filters';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getLocalizedText } from '@/utils/localization';
import { getOptionLabelText } from '@/utils/optionLabel';

export const SelectRevieweeContent = ({
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    reviewees,
    selectedReviewees,
    isLoading,
    selectReviewee,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
    paginationData,
    searchTerm,
    onSearchChange,
}: SelectRevieweeContentProps) => {
    const { t } = useAmelioTranslations();

    const selectedCompanyAttributeOptions = Object.values(selectedCompanyAttributes ?? {});

    const attributeEntries = useMemo(
        () => getAttributeEntriesWithSelectedValues({ selectedCompanyAttributes, filters }),
        [selectedCompanyAttributes, filters],
    );

    const hasNoReviewees =
        paginationData !== undefined && (reviewees.length === 0 || paginationData.totalCount === 0);

    return (
        <Card
            className={'flex flex-col items-center'}
            padding={'xl'}
            borderRadius={'2xl'}
        >
            <Container className={'flex w-full min-w-0 flex-col xl:px-20'}>
                <Container className={'flex w-full flex-col items-center'}>
                    <Container
                        p={'lg'}
                        borderRadius={'full'}
                        className={'bg-primary-50 w-fit'}
                    >
                        <Icon
                            name={'users-people'}
                            size={50}
                            color={Colors.PRIMARY}
                        />
                    </Container>

                    <Container className={'max-w-150'}>
                        <Heading
                            level={1}
                            className={'mt-5 mb-2'}
                            weight={'medium'}
                            align={'center'}
                        >
                            {t('evaluation360SelectReviewee.selectReviewee.title')}
                        </Heading>

                        <Text align={'center'}>
                            {t('evaluation360SelectReviewee.selectReviewee.description')}
                        </Text>
                    </Container>
                </Container>

                {/* Filter */}
                <Container
                    mt={'2xl'}
                    mb={'lg'}
                    gap={'md'}
                    direction={'row'}
                    className={'w-full flex-wrap'}
                >
                    <Container className={'shrink-0'}>
                        <MultiSelectOutlined
                            placeholder={t('common.filter')}
                            icon={
                                <Icon
                                    name={'essential-filter'}
                                    color={Colors.BASE_400}
                                />
                            }
                            options={companyAttributes.map((attr) => ({
                                ...attr,
                                label: decodeAndSanitize(getOptionLabelText(attr.label)),
                            }))}
                            onChange={onSelectCompanyAttribute}
                            values={selectedCompanyAttributeOptions.map((attribute) =>
                                decodeAndSanitize(attribute.name),
                            )}
                            sort={'none'}
                        />
                    </Container>

                    {attributeEntries.map(({ attribute, selectedValues }) => (
                        <div
                            key={attribute.name}
                            className={'max-w-200 min-w-0'}
                        >
                            <Container className={'max-w-full min-w-49.5 shrink-0'}>
                                <MultiSelectOutlined
                                    options={(attribute.values ?? []).map((value) => ({
                                        label: decodeAndSanitize(
                                            'names' in value
                                                ? getLocalizedText(value.names)
                                                : value.name,
                                        ),
                                        value: value.id,
                                    }))}
                                    onChange={(value) =>
                                        onSelectCompanyAttributeOption({ value, attribute })
                                    }
                                    values={selectedValues}
                                    showSelection={true}
                                    label={decodeAndSanitize(attribute.name)}
                                    sort={'asc'}
                                />
                            </Container>
                        </div>
                    ))}

                    <Container className={'w-52'}>
                        <SearchBar
                            placeholder={t('common.search')}
                            onChange={onSearchChange}
                            onClear={() => onSearchChange?.('')}
                            value={searchTerm || ''}
                            size={'md'}
                            data-testid={'search-input'}
                        />
                    </Container>
                </Container>

                {isLoading ? (
                    <Skeleton className={'my-6 flex flex-col gap-6'}>
                        <Container
                            gap={'md'}
                            direction={'row'}
                        >
                            <Bone className={'h-24 flex-1 rounded'} />

                            <Bone className={'h-24 flex-1 rounded'} />

                            <Bone className={'h-24 flex-1 rounded'} />
                        </Container>

                        <Container
                            gap={'md'}
                            direction={'row'}
                        >
                            <Bone className={'h-24 flex-1 rounded'} />

                            <Bone className={'h-24 flex-1 rounded'} />

                            <Bone className={'h-24 flex-1 rounded'} />
                        </Container>
                    </Skeleton>
                ) : (
                    <SelectRevieweeResults
                        hasNoReviewees={hasNoReviewees}
                        reviewees={reviewees}
                        selectedReviewees={selectedReviewees}
                        selectReviewee={selectReviewee}
                    />
                )}

                {/* Pagination */}
                {paginationData && paginationData.totalPages > 1 && (
                    <Pagination
                        variant={'rounded'}
                        showResultsSummary={true}
                        currentPage={paginationData.currentPage}
                        totalPages={paginationData.totalPages}
                        totalCount={paginationData.totalCount}
                        pageSize={paginationData.pageSize}
                        onPageChange={paginationData.onPageChange}
                        isLoading={isLoading}
                        dataTestId={'select-reviewee-pagination'}
                        className={'mt-6'}
                        resultsSummaryText={t('common.showingResults', {
                            start: (paginationData.currentPage - 1) * paginationData.pageSize + 1,
                            end: Math.min(
                                paginationData.currentPage * paginationData.pageSize,
                                paginationData.totalCount,
                            ),
                            total: paginationData.totalCount,
                        })}
                    />
                )}
            </Container>
        </Card>
    );
};
